/**
 * API client functions for the Project Slack Integration.
 * Handles retrieving, saving, and testing project-level
 * Slack webhook configurations.
 */

import { apiClient } from '@/modules/api/api-client';

// --- Types ---

export interface ProjectSlackConfig {
  projectKey: string;
  prEventsWebhookUrl: string | null;
  deployEventsWebhookUrl: string | null;
  prEventTypes: string[];
  deployEventTypes: string[];
  enabled: boolean;
}

export interface TestMessageRequest {
  webhookUrl: string;
}

export interface TestMessageResponse {
  success: boolean;
  error?: string;
}

// --- Slack Config APIs ---

/**
 * Retrieves the Slack integration configuration for a project.
 * Returns a default config (enabled=false, empty URLs/lists) if none exists.
 */
export async function getSlackConfig(projectKey: string): Promise<ProjectSlackConfig> {
  return apiClient.get<ProjectSlackConfig>(
    `/projects/${encodeURIComponent(projectKey)}/slack-config`
  );
}

/**
 * Creates or updates the Slack integration configuration for a project.
 * Validates webhook URLs and project key on the server side.
 */
export async function saveSlackConfig(projectKey: string, config: ProjectSlackConfig): Promise<void> {
  return apiClient.put<void>(
    `/projects/${encodeURIComponent(projectKey)}/slack-config`,
    config
  );
}

/**
 * Sends a test message to the specified Slack webhook URL.
 * Returns success/failure status with an optional error description.
 */
export async function sendTestMessage(projectKey: string, webhookUrl: string): Promise<TestMessageResponse> {
  return apiClient.post<TestMessageResponse>(
    `/projects/${encodeURIComponent(projectKey)}/slack-config/test`,
    { webhookUrl } as TestMessageRequest
  );
}
